import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  getVoiceOptions,
  getVoiceover,
  generateVoiceover,
  getVoiceoverAudioUrl,
  downloadVoiceover,
  VoiceOption,
  VoiceoverInfo,
} from "../services/scriptService";
import { ScriptResponse } from "../types";

const SMALL_BTN =
  "px-3 py-1 rounded-md text-xs transition whitespace-nowrap disabled:opacity-50";

const VoiceoverPanel = ({ script }: { script: ScriptResponse }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canUse = user?.plan === "AGENCY" || (user as any)?.role === "ADMIN";
  const content = script.scriptContent || "";
  const isStory =
    script.scriptFormat === "AI_FILM" ||
    script.scriptFormat === "SHORTS" ||
    content.includes("INT.") ||
    content.includes("EXT.");

  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [voices, setVoices] = useState<VoiceOption[]>([]);
  const [voiceover, setVoiceover] = useState<VoiceoverInfo | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);

  const [mode, setMode] = useState<"DESCRIBE" | "CHOOSE">("DESCRIBE");
  const [description, setDescription] = useState("");
  const [voiceName, setVoiceName] = useState("");
  const [voiceSearch, setVoiceSearch] = useState("");
  const [includeDialogue, setIncludeDialogue] = useState(false);

  const [generating, setGenerating] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState("");

  // Load the voice menu and any saved voiceover the first time the section is opened
  useEffect(() => {
    if (!open || loaded || !canUse) return;
    const load = async () => {
      try {
        const [voiceList, saved] = await Promise.all([
          getVoiceOptions(),
          getVoiceover(script.id),
        ]);
        setVoices(voiceList);
        // Start on the Nigerian female voice if it's there, otherwise the first voice
        const nigerian = voiceList.find(
          (v) => v.voiceName === "en-NG-EzinneNeural",
        );
        setVoiceName(
          nigerian ? nigerian.voiceName : voiceList[0]?.voiceName || "",
        );
        if (saved) {
          setVoiceover(saved);
          setAudioUrl(await getVoiceoverAudioUrl(script.id));
        }
      } catch {
        setError("Could not load voiceover options. Please try again.");
      } finally {
        setLoaded(true);
      }
    };
    load();
  }, [open, loaded, canUse, script.id]);

  // Free the audio from memory when leaving the page
  useEffect(() => {
    return () => {
      if (audioUrl) window.URL.revokeObjectURL(audioUrl);
    };
  }, [audioUrl]);

  // ALL VOICES: filter by the search box, then group by language
  const groupedVoices = useMemo(() => {
    const q = voiceSearch.trim().toLowerCase();
    const filtered = q
      ? voices.filter(
          (v) =>
            v.label.toLowerCase().includes(q) ||
            (v.language || "").toLowerCase().includes(q),
        )
      : voices;
    const groups: Record<string, VoiceOption[]> = {};
    filtered.forEach((v) => {
      const lang = v.language || "Other";
      if (!groups[lang]) groups[lang] = [];
      groups[lang].push(v);
    });
    return groups;
  }, [voices, voiceSearch]);

  const handleGenerate = async () => {
    if (mode === "DESCRIBE" && !description.trim()) {
      setError("Describe the voice you want, or switch to Choose a voice.");
      return;
    }
    if (mode === "CHOOSE" && !voiceName) {
      setError("Choose a voice from the list.");
      return;
    }
    setError("");
    setGenerating(true);
    try {
      const readLines = isStory && includeDialogue;
      const saved =
        mode === "CHOOSE"
          ? await generateVoiceover(script.id, voiceName, undefined, readLines)
          : await generateVoiceover(
              script.id,
              undefined,
              description.trim(),
              readLines,
            );
      setVoiceover(saved);
      if (audioUrl) window.URL.revokeObjectURL(audioUrl);
      setAudioUrl(await getVoiceoverAudioUrl(script.id));
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          "Could not create the voiceover. Please try again.",
      );
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await downloadVoiceover(script.id, script.title);
    } catch {
      setError("Could not download the voiceover. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="bg-gray-900 rounded-xl p-4 border border-gray-700">
      {/* Header: click to open or close */}
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-3 text-left"
      >
        <div>
          <h3 className="text-gray-400 text-xs font-semibold uppercase">
            Voiceover{" "}
            <span className="normal-case font-normal text-gray-500">
              (optional)
            </span>
          </h3>
          <p className="text-gray-500 text-xs mt-1">
            Turn this script into a ready-to-use MP3 narration.
          </p>
        </div>
        <span className="text-gray-400 text-sm">{open ? "▲" : "▼"}</span>
      </button>

      {open && (
        <div className="mt-4 space-y-4">
          {!canUse ? (
            /* Locked for Free and PRO users */
            <div className="bg-gray-800 border border-yellow-600 rounded-lg p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <p className="text-yellow-400 text-sm">
                Voiceovers are an Agency feature.
              </p>
              <button
                onClick={() => navigate("/pricing")}
                className={`${SMALL_BTN} bg-yellow-500 hover:bg-yellow-400 text-black font-semibold`}
              >
                Voiceover (Agency) – Upgrade
              </button>
            </div>
          ) : !loaded ? (
            <p className="text-gray-400 text-sm">Loading voices...</p>
          ) : (
            <>
              {/* Describe or choose */}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setMode("DESCRIBE")}
                  className={`${SMALL_BTN} ${mode === "DESCRIBE" ? "bg-purple-600 text-white" : "bg-gray-800 text-gray-400 hover:bg-gray-700"}`}
                >
                  Describe the voice
                </button>
                <button
                  type="button"
                  onClick={() => setMode("CHOOSE")}
                  className={`${SMALL_BTN} ${mode === "CHOOSE" ? "bg-purple-600 text-white" : "bg-gray-800 text-gray-400 hover:bg-gray-700"}`}
                >
                  Choose a voice
                </button>
              </div>

              {mode === "DESCRIBE" ? (
                <div>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    maxLength={500}
                    rows={2}
                    placeholder="For example: warm, calm Nigerian woman, a bit slow"
                    className="w-full bg-gray-800 border border-gray-600 text-white p-3 rounded-lg focus:outline-none focus:border-purple-500 transition resize-y text-sm"
                  />
                  <p className="text-gray-500 text-xs mt-1">
                    We'll pick the closest voice and adjust its speed and pitch
                    to match.
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  <input
                    type="text"
                    value={voiceSearch}
                    onChange={(e) => setVoiceSearch(e.target.value)}
                    placeholder="Search voices or languages, for example: Nigeria, French, child"
                    className="w-full bg-gray-800 border border-gray-600 text-white px-3 py-2 rounded-lg focus:outline-none focus:border-purple-500 transition text-sm"
                  />
                  <select
                    value={voiceName}
                    onChange={(e) => setVoiceName(e.target.value)}
                    className="w-full bg-gray-800 border border-gray-600 text-white px-3 py-2 rounded-lg focus:outline-none focus:border-purple-500 transition text-sm"
                  >
                    {Object.keys(groupedVoices).length === 0 ? (
                      <option value="">No voices match your search</option>
                    ) : (
                      Object.entries(groupedVoices).map(([language, list]) => (
                        <optgroup key={language} label={language}>
                          {list.map((v) => (
                            <option key={v.voiceName} value={v.voiceName}>
                              {v.label}
                            </option>
                          ))}
                        </optgroup>
                      ))
                    )}
                  </select>
                  <p className="text-gray-500 text-xs">
                    {voices.length} voices available. Pick a voice that matches
                    your script's language.
                  </p>
                </div>
              )}

              {/* Stories only: read the characters' lines, or narrate the action only */}
              {isStory && (
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeDialogue}
                    onChange={(e) => setIncludeDialogue(e.target.checked)}
                    className="mt-1 w-4 h-4 accent-purple-500"
                  />
                  <span className="text-sm text-gray-300">
                    Read the characters' lines
                    <span className="block text-xs text-gray-500">
                      Leave unticked if your characters speak their own lines in
                      the video. Tick it for a story told fully by a narrator.
                    </span>
                  </span>
                </label>
              )}

              {error && (
                <div className="bg-red-900 border border-red-700 text-red-300 px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}

              <button
                type="button"
                onClick={handleGenerate}
                disabled={generating}
                className="w-full bg-purple-600 hover:bg-purple-700 disabled:bg-purple-900 disabled:cursor-not-allowed text-white py-3 rounded-xl font-semibold text-sm transition"
              >
                {generating
                  ? "Creating your voiceover... this can take a minute or two"
                  : voiceover
                    ? "Regenerate voiceover"
                    : "Generate voiceover"}
              </button>

              {/* Saved voiceover: listen and download */}
              {voiceover && audioUrl && !generating && (
                <div className="bg-gray-800 rounded-lg p-4 border border-gray-700 space-y-3">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-gray-300 text-sm">
                      Voice:{" "}
                      <span className="text-purple-400">
                        {voiceover.voiceLabel}
                      </span>
                    </p>
                    <button
                      onClick={handleDownload}
                      disabled={downloading}
                      className={`${SMALL_BTN} bg-purple-600 hover:bg-purple-700 text-white`}
                    >
                      {downloading ? "Preparing..." : "Download MP3"}
                    </button>
                  </div>
                  <audio controls src={audioUrl} className="w-full" />
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default VoiceoverPanel;
